import { useEffect, useRef, useState } from 'react';

import { useUser } from '@cast/infrastructure-auth';
import { useCurrentOrganization } from '@cast/infrastructure-organization';
import { REPORTING_TRIAL_ENABLED } from '@cast/shared-trial-data-access';
import {
  useCanFetchReportingMonetization,
  useReportingTrial,
} from '@cast/shared-trial-ui';

import { clearCastAiWebCookie, getCastAiWebCookie } from 'core/cookies';

import { resolveReportingCookieEligibilityAction } from './resolveReportingCookieEligibilityAction';

const settleReportingCookie = (setHasSettled: (value: boolean) => void) => {
  clearCastAiWebCookie();
  setHasSettled(true);
};

/**
 * Upper bound on waiting for `isEligible` to be reflected after a successful
 * mark. If the trial-status refetch errors or returns stale data, we must
 * still unblock the shell instead of showing the loader forever.
 */
export const REPORTING_ELIGIBILITY_SETTLE_TIMEOUT_MS = 10_000;

/**
 * If `_cast_redirect_context` has `product: 'reporting'`, mark the org eligible
 * then clear the cookie (success or error).
 *
 * Only first-login users on recently created orgs are marked — existing
 * accounts / long-lived orgs clear the cookie without flipping eligibility.
 *
 * Returns true while pending so the app can keep showing a loader until
 * eligibility is reflected (avoids a flash of full Cast AI before the
 * reporting-monetization limit applies). Consume must also wait on this.
 */
export const useMarkReportingEligibleFromCastAiWebCookie = (
  isLoading: boolean
): boolean => {
  const user = useUser();
  const currentOrganization = useCurrentOrganization();
  const {
    isLoading: isTrialLoading,
    isEligible,
    markEligible,
  } = useReportingTrial();
  const { canFetch, isAbilityLoading } = useCanFetchReportingMonetization();
  const didStartMarkRef = useRef(false);
  const [hasSettled, setHasSettled] = useState(false);
  /** After mark-eligible succeeds, wait for status refetch before unblocking UI. */
  const [awaitingEligibility, setAwaitingEligibility] = useState(false);

  useEffect(() => {
    if (awaitingEligibility && isEligible) {
      setHasSettled(true);
      setAwaitingEligibility(false);
    }
  }, [awaitingEligibility, isEligible]);

  // Fail open: if eligibility is never reflected, stop blocking the shell.
  useEffect(() => {
    if (!awaitingEligibility) {
      return;
    }
    if (isEligible) {
      return;
    }
    const timer = setTimeout(() => {
      setAwaitingEligibility(false);
      setHasSettled(true);
    }, REPORTING_ELIGIBILITY_SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [awaitingEligibility, isEligible]);

  useEffect(() => {
    if (isLoading || didStartMarkRef.current || hasSettled) {
      return;
    }

    const cookie = getCastAiWebCookie();
    const needsTrialStatus =
      REPORTING_TRIAL_ENABLED && cookie?.product === 'reporting';
    if (needsTrialStatus && (isTrialLoading || isAbilityLoading)) {
      return;
    }
    // Wait for settled auth + org before deciding. Resolving against
    // undefined user/org would treat firstLogin as false / org as too old,
    // clear the one-shot cookie, and never mark a legitimate signup.
    if (needsTrialStatus && (!user || !currentOrganization)) {
      return;
    }

    const action = resolveReportingCookieEligibilityAction({
      cookie,
      isEligible,
      reportingTrialEnabled: REPORTING_TRIAL_ENABLED,
      canFetchReportingMonetization: canFetch,
      firstLogin: !!user?.firstLogin,
      organizationCreatedAt: currentOrganization?.createdAt,
    });

    if (action === 'noop') {
      setHasSettled(true);
      return;
    }

    if (action === 'clear-only') {
      settleReportingCookie(setHasSettled);
      return;
    }

    didStartMarkRef.current = true;
    markEligible({
      onSuccess: () => {
        clearCastAiWebCookie();
        setAwaitingEligibility(true);
      },
      onError: () => settleReportingCookie(setHasSettled),
    });
  }, [
    canFetch,
    currentOrganization,
    hasSettled,
    isAbilityLoading,
    isEligible,
    isLoading,
    isTrialLoading,
    markEligible,
    user,
  ]);

  const cookie = getCastAiWebCookie();
  return (
    !hasSettled && (cookie?.product === 'reporting' || awaitingEligibility)
  );
};
